import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, ChevronDown, GripVertical, Plus, RotateCcw, Save, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { defaultNavigationLayout, normalizeNavigationLayout, navigationIcons, navigationNodeId, moveNavigationNode } from '@/lib/navigation';
import { updateNavigationLayout } from '@/lib/api';

const iconLabel = (key) => key.replace(/([a-z])([A-Z])/g, '$1 $2');

export default function NavigationEditor({ layout, onClose, onSaved }) {
  const [items, setItems] = useState(() => normalizeNavigationLayout(layout.items));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [iconTarget, setIconTarget] = useState(null);
  const [iconSearch, setIconSearch] = useState('');
  const [dragging, setDragging] = useState(null);
  const [resetPending, setResetPending] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [focusTopic, setFocusTopic] = useState(null);
  const [announcement, setAnnouncement] = useState('');
  const panel = useRef(null);
  const iconTrigger = useRef(null);
  const groups = items.filter((item) => item.type === 'group');
  const all = items.flatMap((item) => [item, ...(item.children || [])]);
  useEffect(() => {
    if (!focusTopic) return;
    const row = Array.from(panel.current?.querySelectorAll('[data-navigation-id]') || [])
      .find((element) => element.dataset.navigationId === focusTopic);
    row?.scrollIntoView?.({ block: 'nearest' });
    row?.querySelector('input')?.focus();
    row?.querySelector('input')?.select();
    setFocusTopic(null);
  }, [focusTopic]);
  const update = (id, values) => {
    setItems((previous) => previous.map((item) => ({ ...item,
      ...(navigationNodeId(item) === id ? values : {}),
      ...(item.children ? { children: item.children.map((child) => navigationNodeId(child) === id ? { ...child, ...values } : child) } : {}),
    })));
    setResetPending(false);
  };
  const move = (id, parent, before) => {
    setItems((previous) => moveNavigationNode(previous, id, parent, before));
    setResetPending(false);
    if (parent) setExpanded((previous) => ({ ...previous, [parent]: true }));
    const label = all.find((item) => navigationNodeId(item) === id)?.label;
    setAnnouncement(`${label || 'Item'} moved ${parent ? `within ${groups.find((group) => group.id === parent)?.label || 'topic'}` : 'within the main menu'}.`);
  };
  const removeGroup = (id) => {
    setItems((previous) => previous.flatMap((item) => item.id === id ? item.children : [item]));
    setResetPending(false);
  };
  const save = async () => {
    if (all.some((item) => !item.label.trim())) { setError('Give every topic and page a name before saving.'); return; }
    setSaving(true); setError('');
    try {
      const { data } = await updateNavigationLayout({ revision: layout.revision, items: resetPending ? null : items });
      onSaved(data);
      toast.success('Navigation saved for administrators');
    } catch (failure) {
      const detail = failure?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Navigation could not be saved. Your changes are still here; please try again.');
    } finally { setSaving(false); }
  };
  const drop = (event, parent = null, before = null) => {
    event.preventDefault(); event.stopPropagation();
    const id = event.dataTransfer?.getData('text/plain') || dragging;
    if (id) move(id, parent, before);
    setDragging(null);
  };
  const renderRow = (item, index, siblings, parent = null) => {
    const id = navigationNodeId(item);
    const Icon = navigationIcons[item.icon] || navigationIcons.Folder;
    return (
      <div key={id} data-navigation-id={id} className="min-w-0 rounded-xl border border-slate-200 bg-white">
        <div onDragOver={(event) => event.preventDefault()} onDrop={(event) => drop(event, parent, id)}
          className={`flex flex-wrap items-center gap-2 rounded-lg p-2 ${item.children ? 'bg-slate-50' : 'bg-white'} ${dragging === id ? 'opacity-50' : ''}`}>
          <button type="button" draggable={!saving} onDragStart={(event) => { event.dataTransfer.setData('text/plain', id); event.dataTransfer.effectAllowed = 'move'; setDragging(id); }}
            onDragEnd={() => setDragging(null)} aria-label={`Drag ${item.label}`} title="Drag to reorder"
            className="cursor-grab rounded p-1 text-slate-400 focus-visible:ring-2 focus-visible:ring-primary">
            <GripVertical className="size-4" />
          </button>
          {item.children && <button type="button" aria-label={`${expanded[id] ? 'Collapse' : 'Expand'} topic ${item.label}`} aria-expanded={!!expanded[id]}
            aria-controls={`pages-${id}`} onClick={() => setExpanded((previous) => ({ ...previous, [id]: !previous[id] }))}
            className="rounded p-1 text-slate-600 hover:bg-slate-200 focus-visible:ring-2 focus-visible:ring-primary">
            <ChevronDown className={`size-4 transition-transform ${expanded[id] ? '' : '-rotate-90'}`} />
          </button>}
          <button type="button" onClick={(event) => { iconTrigger.current = event.currentTarget; setIconTarget(id); setIconSearch(''); }} aria-label={`Choose icon for ${item.label}`}
            className="rounded-lg border border-slate-200 bg-white p-2 text-[#0F5132] hover:bg-slate-100"><Icon className="size-4" /></button>
          <Input aria-label={`Name for ${item.label}`} value={item.label} maxLength={60} onChange={(event) => update(id, { label: event.target.value })}
            className="min-w-0 flex-1 basis-28 bg-white" />
          <div className="flex w-full items-center justify-end gap-2 sm:w-auto">
          {item.children ? <span className="mr-auto whitespace-nowrap px-2 text-xs text-slate-500 sm:mr-0">{item.children.length} pages</span> :
            <label className="mr-auto flex min-w-0 items-center gap-2 text-xs text-slate-500 sm:mr-0">
              <span className="whitespace-nowrap">Move to</span>
              <select aria-label={`Topic for ${item.label}`} value={parent || ''} onChange={(event) => move(id, event.target.value || null)}
                className="h-9 w-32 min-w-0 rounded-lg border border-slate-200 bg-white px-2 text-xs text-slate-700 sm:w-36">
                <option value="">Main menu</option>
                {groups.map((group) => <option key={group.id} value={group.id}>{group.label}</option>)}
              </select>
            </label>}
          <div className="flex shrink-0 items-center">
            <Button type="button" variant="ghost" size="icon" aria-label={`Move ${item.label} up`} disabled={index === 0}
              onClick={() => move(id, parent, navigationNodeId(siblings[index - 1]))}><ArrowUp /></Button>
            <Button type="button" variant="ghost" size="icon" aria-label={`Move ${item.label} down`} disabled={index === siblings.length - 1}
              onClick={() => move(id, parent, siblings[index + 2] ? navigationNodeId(siblings[index + 2]) : null)}><ArrowDown /></Button>
            {item.children && <Button type="button" variant="ghost" size="icon" aria-label={`Remove topic ${item.label}`} title="Remove topic; keep its pages in the main menu"
              onClick={() => removeGroup(id)}><Trash2 className="text-slate-500" /></Button>}
          </div>
          </div>
        </div>
        {item.children && expanded[id] && <div id={`pages-${id}`} className="space-y-2 border-t p-2 sm:pl-5" onDragOver={(event) => event.preventDefault()} onDrop={(event) => drop(event, id)}>
          {item.children.map((child, childIndex) => renderRow(child, childIndex, item.children, id))}
          <p className="px-3 py-2 text-xs text-slate-500">{item.children.length ? 'Drop a page here to move it into this topic.' : 'Move a page here using its topic menu, or drag it here. Empty topics stay out of the sidebar.'}</p>
        </div>}
      </div>
    );
  };
  const selected = all.find((item) => navigationNodeId(item) === iconTarget);
  return <Dialog open onOpenChange={(open) => { if (!open && !saving) onClose(); }}>
    <DialogContent className="flex h-[calc(100dvh-2rem)] max-h-[900px] w-[calc(100%-1rem)] max-w-4xl flex-col gap-3 overflow-hidden rounded-xl p-3 sm:p-6" onEscapeKeyDown={(event) => { if (saving) event.preventDefault(); }}>
      <DialogHeader className="shrink-0 pr-6 text-left">
        <DialogTitle>Customize navigation</DialogTitle>
        <DialogDescription>Expand a topic to manage its pages. Use Move to and the arrows, or drag to reorder. Changes apply to administrators after saving.</DialogDescription>
      </DialogHeader>
      {error && <p role="alert" className="max-h-24 shrink-0 overflow-y-auto rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
        <Button type="button" variant="outline" disabled={saving || groups.length >= 30} onClick={() => {
          const id = `topic-${globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;
          setItems((previous) => [{ type: 'group', id, label: 'New topic', icon: 'Folder', children: [] }, ...previous]);
          setExpanded((previous) => ({ ...previous, [id]: true }));
          setFocusTopic(id); setResetPending(false);
        }}><Plus /> New topic</Button>
        <div className="flex gap-1">
          <Button type="button" size="sm" variant="ghost" disabled={saving} onClick={() => setExpanded(Object.fromEntries(groups.map((group) => [group.id, true])))}>Expand all</Button>
          <Button type="button" size="sm" variant="ghost" disabled={saving} onClick={() => setExpanded({})}>Collapse all</Button>
        </div>
      </div>
      {/* A fieldset cannot reliably be the flex scroll owner in Chromium. Keep it inside a bounded div. */}
      <div ref={panel} role="region" aria-label="Navigation items" tabIndex={0}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain rounded-xl border border-slate-200 bg-slate-50/60 p-2 sm:p-3"
        style={{ scrollbarGutter: 'stable' }} onDragOver={(event) => {
          if (!dragging) return;
          event.preventDefault();
          const bounds = event.currentTarget.getBoundingClientRect();
          if (event.clientY < bounds.top + 60) event.currentTarget.scrollBy({ top: -20 });
          else if (event.clientY > bounds.bottom - 60) event.currentTarget.scrollBy({ top: 20 });
        }}>
        <fieldset disabled={saving} className="min-w-0 space-y-3" onDragOver={(event) => event.preventDefault()} onDrop={(event) => drop(event)}>
          {items.map((item, index) => renderRow(item, index, items))}
          <div className="rounded-lg border border-dashed p-3 text-center text-xs text-slate-500">Drop here to move to the end of the main menu</div>
        </fieldset>
      </div>
      <p role="status" className="sr-only">{announcement}</p>
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t pt-3">
        <Button type="button" variant="ghost" disabled={saving} onClick={() => { setItems(defaultNavigationLayout()); setResetPending(true); setExpanded({}); setIconTarget(null); setError(''); panel.current?.scrollTo?.({ top: 0 }); }}><RotateCcw /> Restore defaults</Button>
        <div className="flex gap-2"><Button type="button" variant="outline" disabled={saving} onClick={onClose}>Cancel</Button><Button type="button" disabled={saving} onClick={save}><Save /> {saving ? 'Saving…' : 'Save navigation'}</Button></div>
      </div>
      <Dialog open={!!selected} onOpenChange={(open) => { if (!open) setIconTarget(null); }}>
        <DialogContent className="flex max-h-[calc(100dvh-2rem)] w-[calc(100%-1rem)] max-w-lg flex-col overflow-hidden rounded-xl p-4 sm:p-6"
          onCloseAutoFocus={(event) => { event.preventDefault(); iconTrigger.current?.focus(); }}>
          <DialogHeader className="shrink-0 pr-6 text-left">
            <DialogTitle>Choose an icon</DialogTitle>
            <DialogDescription>Icon for {selected?.label}. Select an icon to apply it to your draft.</DialogDescription>
          </DialogHeader>
          <Input aria-label="Search icons" placeholder="Search icons…" className="shrink-0" value={iconSearch} onChange={(event) => setIconSearch(event.target.value)} />
          <div role="region" aria-label="Available icons" className="grid min-h-0 grid-cols-4 gap-1 overflow-y-auto overscroll-contain sm:grid-cols-5">
            {Object.entries(navigationIcons).filter(([key]) => iconLabel(key).toLowerCase().includes(iconSearch.toLowerCase())).map(([key, Icon]) =>
              <button type="button" key={key} aria-label={iconLabel(key)} aria-pressed={selected?.icon === key} title={iconLabel(key)} onClick={() => { update(iconTarget, { icon: key }); setIconTarget(null); }}
                className={`flex min-h-16 flex-col items-center justify-center gap-1 rounded-lg border p-2 text-[10px] ${selected?.icon === key ? 'border-[#0F5132] bg-green-50 text-[#0F5132]' : 'border-transparent hover:bg-slate-100'}`}><Icon className="size-5" /><span>{iconLabel(key)}</span></button>)}
            {!Object.keys(navigationIcons).some((key) => iconLabel(key).toLowerCase().includes(iconSearch.toLowerCase())) && <p className="col-span-full py-6 text-center text-sm text-slate-500">No icons match your search.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </DialogContent>
  </Dialog>;
}
